import React from "react";
import { createPortal } from "react-dom";
import Icon from "@mdi/react";
import { mdiBackupRestore, mdiClose, mdiSwapHorizontal } from "@mdi/js";
import { WarehouseOutwardModel } from "../model/warehouse_outward_model";

type Props = {
  open: boolean;
  outward: WarehouseOutwardModel | null;
  busy?: boolean;
  onClose: () => void;
  onRevertForOrderUpdate: () => void;
  onReassignWarehouseOnly: () => void;
};

const RevertOptionsModal: React.FC<Props> = ({
  open,
  outward,
  busy,
  onClose,
  onRevertForOrderUpdate,
  onReassignWarehouseOnly,
}) => {
  if (!open || !outward) return null;

  return createPortal(
    <div
      id="warehouse-outward-revert-options-modal"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 p-4 overflow-y-auto"
      style={{ top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div
        className="relative w-full max-w-lg my-auto flex flex-col rounded-xl bg-white shadow-xl overflow-hidden wh-modal-xs"
        style={{ maxHeight: "90vh" }}
      >
        <div className="flex items-center justify-between border-b px-6 py-4 shrink-0">
          <div>
            <h2 className="text-lg font-semibold">Revert outward</h2>
            <p className="text-xs text-gray-500">
              {"order_id" in outward && outward.order_id
                ? `Order ${outward.order_id} · `
                : ""}
              Outward {outward.id}
            </p>
          </div>
          <button
            id="warehouse-outward-revert-options-close-icon-btn"
            onClick={onClose}
            disabled={busy}
            className={`rounded-full p-1 ${
              busy ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"
            }`}
          >
            <Icon path={mdiClose} size={0.9} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-3">
          <button
            id="warehouse-outward-revert-for-order-update-btn"
            type="button"
            onClick={onRevertForOrderUpdate}
            disabled={busy}
            className={`w-full text-left rounded-lg border p-4 transition-colors ${
              busy
                ? "opacity-50 cursor-not-allowed"
                : "hover:border-amber-500 hover:bg-amber-50"
            }`}
          >
            <div className="flex items-start gap-3">
              <Icon
                path={mdiBackupRestore}
                size={1}
                className="text-amber-600 mt-0.5 shrink-0"
              />
              <div>
                <p className="font-semibold text-gray-900">
                  Revert for order update
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Releases the warehouse and devices; the orders team must edit
                  and re-approve before a new warehouse can be assigned.
                </p>
              </div>
            </div>
          </button>

          <button
            id="warehouse-outward-reassign-warehouse-only-btn"
            type="button"
            onClick={onReassignWarehouseOnly}
            disabled={busy}
            className={`w-full text-left rounded-lg border p-4 transition-colors ${
              busy
                ? "opacity-50 cursor-not-allowed"
                : "hover:border-blue-500 hover:bg-blue-50"
            }`}
          >
            <div className="flex items-start gap-3">
              <Icon
                path={mdiSwapHorizontal}
                size={1}
                className="text-blue-600 mt-0.5 shrink-0"
              />
              <div>
                <p className="font-semibold text-gray-900">
                  Reassign warehouse only
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Releases the warehouse and devices so a new warehouse can be
                  picked immediately, leaving the order untouched.
                </p>
              </div>
            </div>
          </button>
        </div>

        <div className="border-t px-6 py-3 shrink-0 flex justify-end">
          <button
            id="warehouse-outward-revert-options-cancel-btn"
            onClick={onClose}
            disabled={busy}
            className={`h-9 px-4 rounded-md border border-gray-300 text-sm ${
              busy
                ? "opacity-50 cursor-not-allowed"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default RevertOptionsModal;
